import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { BlattFuss, BlattHintergrund, BlattKopf } from './FilterBlatt';

/**
 * Ein Feld der Suchzeile über der Liste (Startseite am Rechner): Beschriftung klein, Wert groß, wie
 * „Check In · Thu, May 01“ bei Tripadvisor. Entweder ein Knopf (`onClick`, etwa für den Ort-Dialog)
 * oder ein Menü mit festen Möglichkeiten (`options`).
 *
 * Drei solche Felder ersetzen die Reihe aus zehn Pillen, die vorher über Liste und Karte lief: Ort,
 * Zeitraum und Disziplin sind die drei Fragen, die jeder Besucher beantwortet; der Rest liegt hinter
 * „Filter“.
 */
export interface SuchFeldOption { value: string; label: string; marker?: React.ReactNode }

interface Props {
  icon: React.ReactNode;
  label: string;
  value: string;
  /** Hervorgehoben, wenn ein Wert gesetzt ist (nicht der Ausgangszustand). */
  active?: boolean;
  onClick?: () => void;
  options?: SuchFeldOption[];
  selected?: string;
  onSelect?: (value: string) => void;
  /** Gesetzt und aktiv: ein X im Feld setzt den Wert in einem Klick zurück (statt des Pfeils). */
  onClear?: () => void;
  /** Zusatz unten im Menü (etwa ein Datumsfeld); `schliessen` klappt das Menü zu. */
  extra?: (schliessen: () => void) => React.ReactNode;
  /** Als kleiner Knopf (Handy) statt als großes Feld. */
  pille?: boolean;
  /** Trefferzahl für den Knopf unten im Blatt (Handy). */
  treffer?: number;
  /** Menüpunkte als Knöpfe nebeneinander statt als Liste (Zeitraum: Schnellwahl über dem Kalender). */
  knoepfe?: boolean;
}

const SuchFeld: React.FC<Props> = ({ icon, label, value, active, onClick, options, selected, onSelect, onClear, extra, pille, treffer, knoepfe }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  return (
    <div ref={ref} className={pille ? 'filter-pille-wrap' : 'such-feld-wrap'}>
      {pille ? (
        <button type="button" className={`filter-pille${active ? ' filter-pille--an' : ''}`}
          aria-haspopup={options ? 'listbox' : undefined} aria-expanded={options ? open : undefined}
          onClick={() => (options ? setOpen(o => !o) : onClick?.())}>
          {active ? value : label}
          {options && !active && <ChevronDown size={15} aria-hidden />}
        </button>
      ) : (
        <button type="button" className={`such-feld${active ? ' such-feld--aktiv' : ''}`}
          aria-haspopup={options ? 'listbox' : undefined} aria-expanded={options ? open : undefined}
          onClick={() => (options ? setOpen(o => !o) : onClick?.())}>
          <span className="such-feld-icon" aria-hidden>{icon}</span>
          <span className="such-feld-text">
            <span className="such-feld-label">{label}</span>
            <span className="such-feld-wert">{value}</span>
          </span>
          {options && !(active && onClear) && <ChevronDown size={16} aria-hidden className="such-feld-pfeil" />}
        </button>
      )}
      {active && onClear && (
        <button type="button" className={pille ? 'filter-pille-x' : 'such-feld-x'} onClick={() => { onClear(); setOpen(false); }} aria-label={`${label} zurücksetzen`}><X size={pille ? 14 : 16} aria-hidden /></button>
      )}
      {options && open && <BlattHintergrund onClose={() => setOpen(false)} />}
      {options && open && (
        <ul className={`such-feld-menue${knoepfe ? ' such-feld-menue--knoepfe' : ''}`} role="listbox" aria-label={label}>
          <li role="presentation"><BlattKopf titel={label} onClose={() => setOpen(false)} /></li>
          {options.map(o => (
            <li key={o.value} role="option" aria-selected={o.value === selected}>
              <button type="button" onClick={() => { onSelect?.(o.value); setOpen(false); }}>
                {o.marker}
                <span>{o.label}</span>
                {o.value === selected && <Check size={16} aria-hidden className="such-feld-haken" />}
              </button>
            </li>
          ))}
          {extra && <li className="such-feld-extra" role="presentation">{extra(() => setOpen(false))}</li>}
          <li className="such-feld-fuss" role="presentation">
            <BlattFuss treffer={treffer} onZuruecksetzen={active && onClear ? () => onClear() : undefined} onFertig={() => setOpen(false)} />
          </li>
        </ul>
      )}
    </div>
  );
};

export default SuchFeld;
